import { useCallback, useEffect, useState } from 'react';

import { ApiErrorData } from 'models/ApiResponse';
import {
  TiendaPerfectaDistributor,
  TiendaPerfectaDistributorsResponse,
} from 'models/TiendaPerfecta';

import { TiendaPerfectaService } from 'services/TiendaPerfectaService';

import { VANS_BLOCKED_REASON_BY_CODE, VansBlockedReason } from '../types';

interface UseVansScopeReturn {
  loading: boolean;
  distributors: TiendaPerfectaDistributor[];
  selectedDistributorId: string | undefined;
  setSelectedDistributorId: (id: string) => void;
  /** `true` cuando el usuario alcanza un solo distribuidor: no hay qué elegir. */
  singleDistributor: boolean;
  blockedReason: VansBlockedReason | null;
}

/**
 * Resuelve el alcance del usuario: qué distribuidores puede ver y, si no puede
 * ver ninguno, por qué. `/distributors/scoped` ya aplica el tier de los roles,
 * así que la vista no necesita conocerlo.
 */
export function useVansScope(): UseVansScopeReturn {
  const [loading, setLoading] = useState(true);
  const [distributors, setDistributors] = useState<TiendaPerfectaDistributor[]>(
    []
  );
  const [selectedDistributorId, setSelectedDistributorId] = useState<
    string | undefined
  >(undefined);
  const [blockedReason, setBlockedReason] = useState<VansBlockedReason | null>(
    null
  );

  const fetchScope = useCallback(async () => {
    setLoading(true);

    const response = await TiendaPerfectaService.getScopedDistributors({
      sortBy: 'tradeName',
      sortDir: 'asc',
    });

    if (response.success) {
      const list =
        (response.data as TiendaPerfectaDistributorsResponse)?.distributors ??
        [];

      setDistributors(list);
      // Con acceso pero sin distribuidores asignados no hay error: es un 200
      // con lista vacía, y aun así no hay flota que mostrar.
      setBlockedReason(list.length === 0 ? 'no_distributors' : null);
      setSelectedDistributorId(list[0]?.id);
    } else {
      const error = response.data as ApiErrorData;
      // Se ramifica por `code`, nunca por el mensaje: el texto puede cambiar y
      // no está garantizado en español.
      setBlockedReason(VANS_BLOCKED_REASON_BY_CODE[error?.code] ?? 'generic');
      setDistributors([]);
      setSelectedDistributorId(undefined);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchScope();
  }, [fetchScope]);

  return {
    loading,
    distributors,
    selectedDistributorId,
    setSelectedDistributorId,
    singleDistributor: distributors.length === 1,
    blockedReason,
  };
}
